import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { toast } from "sonner";

/**
 * Hook to bulk approve expenses.
 * Invalidates pending and approved expense queries on success.
 */
export function useBulkApproveExpenses() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...trpc.expense.bulkApproveExpenses.mutationOptions(),
    onSuccess: (data) => {
      // Invalidate both pending and approved queries
      queryClient.invalidateQueries({
        queryKey: trpc.expense.getPendingExpensesForOrg.queryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.expense.getApprovedExpensesForOrg.queryKey(),
      });

      toast.success(
        `Successfully approved ${data.approved} expense${
          data.approved !== 1 ? "s" : ""
        }`,
      );
    },
    onError: (error) => {
      toast.error(error.message || "Failed to approve expenses");
    },
  });

  return mutation;
}

/**
 * Hook to reject an expense with a note.
 * Invalidates pending expense queries on success.
 */
export function useRejectExpense() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...trpc.expense.rejectExpense.mutationOptions(),
    onSuccess: () => {
      // Invalidate pending queries
      queryClient.invalidateQueries({
        queryKey: trpc.expense.getPendingExpensesForOrg.queryKey(),
      });

      toast.success("Expense rejected");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to reject expense");
    },
  });

  return mutation;
}
